import type React from 'react'
import { CloseIcon, LockIcon } from '../icons'
import { cn, cva } from '../lib/cva'
import {
	type BaseFormElementProps,
	type FormElementStyleProps,
	FormElementWrapper,
	formStyles,
} from './FormElementWrapper'

interface Tag {
	id: string
	label: string
}

interface TagFieldProps extends BaseFormElementProps {
	value?: Tag[]
	defaultValue?: Tag[]
	onChange?: (tags: Tag[]) => void
	placeholder?: string
	readonly?: boolean
	lockView?: boolean
	hint?: React.ReactNode
}

const tagFieldStyles = {
	tag: cva('flex items-center gap-1 bg-neutral-100 text-neutral-800 py-1 px-2 rounded-sm text-sm max-w-full', {
		variants: {
			disabled: {
				true: 'bg-neutral-100 text-neutral-400',
				false: '',
			},
		},
		defaultVariants: {
			disabled: false,
		},
	}),
	tagLabel: 'truncate',
}

export const TagField: React.FC<TagFieldProps> = ({
	label,
	hint,
	showHintIcon = false,
	variant = 'default',
	disabled = false,
	className,
	value: controlledTags,
	defaultValue = [],
	onChange,
	placeholder = 'Add tag...',
	isRequired = false,
	readonly = false,
	lockView = false,
}) => {
	const resolvedVariant =
		variant === 'error' ? 'error' : (controlledTags || defaultValue).length > 0 ? 'filled' : 'default'
	const styleProps: FormElementStyleProps = {
		variant: resolvedVariant,
		disabled,
		isFocused: false,
		hasValue: (controlledTags || defaultValue).length > 0,
	}

	const renderTags = () => {
		const tags = controlledTags || defaultValue
		return (
			<div
				className={cn(
					formStyles.element({
						variant: styleProps.variant,
						isFocused: styleProps.isFocused,
						disabled: styleProps.disabled,
						readOnly: readonly,
					}),
					'flex flex-wrap items-center gap-2 bg-neutrals-0 flex-grow',
					className,
				)}>
				{tags.map((tag) => (
					<div
						key={tag.id}
						className={tagFieldStyles.tag({
							disabled: styleProps.disabled,
						})}>
						<span className={tagFieldStyles.tagLabel} title={tag.label}>
							{tag.label}
						</span>

						{lockView && <LockIcon size={16} className='text-neutral-500' />}

						{!styleProps.disabled && (
							<button
								type='button'
								onClick={(e) => {
									e.stopPropagation()
									if (onChange) {
										const updatedTags = tags.filter((t) => t.id !== tag.id)
										onChange(updatedTags)
									}
								}}
								className='focus:outline-none bg-neutral-200 hover:bg-neutral-300 rounded-sm cursor-pointer flex-shrink-0'>
								{!readonly && <CloseIcon size={16} />}
							</button>
						)}
					</div>
				))}
				{tags.length === 0 && <span className='text-neutral-500'>{placeholder}</span>}
			</div>
		)
	}

	if (readonly) {
		return renderTags()
	}

	return (
		<FormElementWrapper
			label={label}
			hint={hint}
			showHintIcon={showHintIcon}
			variant={variant}
			disabled={disabled}
			isRequired={isRequired}
			styleProps={styleProps}>
			{renderTags()}
		</FormElementWrapper>
	)
}
